// 页：⑦ 反馈学习
// 4 tab：定价学习 / listing RAG 学习 / 图片学习 / 效果回流
// 核心范式（柴进 2026-06-27 拍板）：
//   - 反馈不是单条 RLHF，是累积聚合给上游
//     · 图片 → prompt 模板（戒指品类）+ 选款降权
//     · listing → RAG 召回库范本集 + 高权重池
//     · 定价 → 倍率系数重训 + K 近邻借相似款
//     · 效果 → 选款 source_type 权重调整
//   - 不 fine-tune 模型，只动召回库 + 配置层

import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Tag, Table, Progress, Alert, Tooltip, Divider, Empty, Timeline } from 'antd';
import { Tabs } from '../AntdCompat';
import {
  ApiOutlined, CheckCircleTwoTone, WarningTwoTone, CloseCircleTwoTone, BulbOutlined,
  ArrowUpOutlined, ArrowDownOutlined, MinusOutlined,
  DollarOutlined, FileTextOutlined, PictureOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  priceLearningSnapshot, listingLearningSnapshot, imageLearningSnapshot, effectFeedbackSnapshot,
  REJECT_REASON_LABELS,
} from '../data';
import { SourceType, StyleTag, RejectReasonCode } from '../types';

const SpecCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert
    type="info"
    showIcon
    icon={<ApiOutlined />}
    message={<span style={{ fontSize: 12 }}>{children}</span>}
    style={{ marginBottom: 8 }}
  />
);
const BizCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert
    type="success"
    showIcon
    icon={<BulbOutlined />}
    message={<span style={{ fontSize: 12 }}><strong>业务方怎么用：</strong>{children}</span>}
    style={{ marginBottom: 12 }}
  />
);

const FeedbackLearning: React.FC = () => {
  const pls = priceLearningSnapshot;
  const lls = listingLearningSnapshot;
  const ils = imageLearningSnapshot;
  const efs = effectFeedbackSnapshot;
  const [overrideStyle, setOverrideStyle] = useState<string>('all');

  return (
    <div style={{ padding: '16px 24px' }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row align="middle" gutter={12}>
          <Col flex="120px"><strong>♻️ 反馈学习</strong></Col>
          <Col flex="auto">
            <span style={{ fontSize: 11, color: '#8c8c8c' }}>
              spec §4.6 / §4.7 / §4.8 反馈回路 · <strong>反馈不给单条，给上游</strong>（图片→prompt 模板 + 选款，listing→RAG 召回库，定价→倍率重训）· 不 fine-tune 模型
            </span>
          </Col>
        </Row>
      </Card>

      <Tabs
        items={[
          // ============================================================
          // Tab 1：定价学习
          // ============================================================
          {
            key: 'price',
            label: '① 定价学习',
            children: (
              <>
                <SpecCallout>
                  spec §4.8：不学历史绝对价，学倍率系数。<strong>有同款历史成交</strong>：学 <code>定价 ÷ (金价 × 克重)</code>；<strong>无同款新款</strong>：CLIP 嵌入 + K 近邻借相似款的 pureGoldWeight / mainStoneValue 系数。AI 输出三档（保守/建议/测试）+ 依据来源（运营一眼看 AI 有根据还是瞎猜）。代码现状：<code>EpsFactoryOrderAssembler.getSuggestedRetailPrice()</code> 倍率体系已就绪；spec 缺三段：金价订阅器 / 物料级成本合成 / 三档表扩展
                </SpecCallout>
                <BizCallout>
                  这页解决一个问题——"AI 给的价能不能用？" 头条 4 项验收看模型是否达 spec 标准；
                  <strong>三站回归</strong>（柴进 0627 拍板）拿 FG/ST/BF 现有商品定价做 ground truth 回归判断 AI 准确性 — MAE 越低越准；
                  override 趋势看运营在哪些品类/价位频繁改价，反推训练集偏差。
                </BizCallout>

                {/* spec §4.8 验收 4 项 */}
                <Card size="small" title="📋 spec §4.8 验收 4 项" style={{ marginBottom: 12 }}>
                  <Row gutter={12}>
                    <Col span={6}>
                      <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>① 倍率系数 MAE</div>
                        <Statistic
                          value={pls.acceptance.multiplier_mae_pct.toFixed(1)}
                          suffix="%"
                          valueStyle={{ color: pls.acceptance.multiplier_mae_pct < 15 ? '#52c41a' : '#cf1322' }}
                          prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                        />
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>目标 &lt; 15% ✓</div>
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>② 100% 带依据</div>
                        <Statistic
                          value={(pls.acceptance.has_evidence_rate * 100).toFixed(0)}
                          suffix="%"
                          valueStyle={{ color: '#52c41a' }}
                          prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                        />
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>K 近邻列表必填</div>
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>③ 金价订阅延迟</div>
                        <Statistic
                          value={pls.acceptance.gold_subscriber_lag_minutes.toFixed(1)}
                          suffix="min"
                          valueStyle={{ color: '#52c41a' }}
                          prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                        />
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>分钟级 ✓（spec 缺口 a 已补）</div>
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>④ 三档价接受率</div>
                        <Statistic
                          value={(pls.acceptance.accept_band_hit_rate * 100).toFixed(0)}
                          suffix="%"
                          valueStyle={{ color: pls.acceptance.accept_band_hit_rate >= 0.8 ? '#52c41a' : '#fa8c16' }}
                          prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                        />
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>≥80% ✓ — 至少 1 档落运营接受区间</div>
                      </Card>
                    </Col>
                  </Row>
                </Card>

                {/* 三站回归测试 — 柴进 0627 拍板 */}
                <Card size="small" title={<span>🎯 FG / ST / BF 三站定价回归测试（评估手段·柴进 2026-06-27 拍板）</span>} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#595959', marginBottom: 12 }}>
                    把三站现有商品的<strong>真实成交价</strong>作为 ground truth，让 AI 重新算一遍，看偏差。这是判断"AI 定价准不准"的最直接评估手段。
                    <Tag color="warning" style={{ marginLeft: 8 }}>样本来自 saas_item.eps_commodity</Tag>
                  </div>
                  <Row gutter={12}>
                    {pls.site_regression.map(s => (
                      <Col span={8} key={s.site}>
                        <Card size="small" type="inner" title={<span><strong>{s.site}</strong> 站 · n={s.sample_count}</span>} extra={
                          <Tag color={s.mae_pct < 15 ? 'success' : 'warning'}>{s.mae_pct < 15 ? '过' : '观察'}</Tag>
                        }>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                            <span>MAE</span>
                            <strong style={{ color: s.mae_pct < 15 ? '#52c41a' : '#fa8c16' }}>{s.mae_pct.toFixed(1)}%</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                            <span>接受区间命中</span>
                            <strong>{(s.accept_band_hit * 100).toFixed(0)}%</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                            <span>平均置信度</span>
                            <strong>{(s.avg_confidence * 100).toFixed(0)}%</strong>
                          </div>
                          <Divider plain style={{ margin: '8px 0', fontSize: 10 }}>偏差最大 case</Divider>
                          {s.notable_misses.map((m, i) => (
                            <div key={i} style={{ fontSize: 10, marginBottom: 6, padding: 6, background: '#fafafa', borderRadius: 3 }}>
                              <div style={{ fontFamily: 'monospace' }}>{m.commodity_no}</div>
                              <div>
                                真 <strong>${m.real_usd.toFixed(2)}</strong> vs AI <strong style={{ color: '#fa8c16' }}>${m.ai_recommend_usd.toFixed(2)}</strong>
                                <Tag color={Math.abs(m.deviation_pct) > 25 ? 'red' : 'orange'} style={{ marginLeft: 4, fontSize: 9 }}>
                                  {m.deviation_pct > 0 ? '+' : ''}{m.deviation_pct.toFixed(1)}%
                                </Tag>
                              </div>
                              <div style={{ color: '#8c8c8c', marginTop: 2 }}>💡 {m.reason_guess}</div>
                            </div>
                          ))}
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>

                {/* 运营 override 趋势 */}
                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={12}>
                    <Card size="small" title="按风格的 override 率（运营改价频繁的品类）">
                      {pls.override_by_style.sort((a, b) => b.rate - a.rate).map(o => (
                        <div key={o.style} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <span><Tag color="blue">{o.style}</Tag> {o.total} 候选 · 改 {o.overridden}</span>
                            <span><strong>{(o.rate * 100).toFixed(1)}%</strong> <small style={{ color: '#8c8c8c' }}>偏差 {o.avg_deviation_pct.toFixed(1)}%</small></span>
                          </div>
                          <Progress percent={o.rate * 100} size="small" showInfo={false} strokeColor={o.rate > 0.25 ? '#cf1322' : o.rate > 0.15 ? '#fa8c16' : '#52c41a'} />
                        </div>
                      ))}
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                        🎯 override 率高 = 该品类训练集需要补充 → 反馈给 K 近邻特征工程 + 倍率重训
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="按价格区间的 override 率">
                      {pls.override_by_price_range.map(p => (
                        <div key={p.range} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <span>{p.range}</span>
                            <span>{p.count} 候选 · 改 {p.overridden} · <strong>{(p.rate * 100).toFixed(1)}%</strong></span>
                          </div>
                          <Progress percent={p.rate * 100} size="small" showInfo={false} strokeColor={p.rate > 0.3 ? '#cf1322' : p.rate > 0.2 ? '#fa8c16' : '#52c41a'} />
                        </div>
                      ))}
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                        🎯 $0-100 和 $800+ override 率高 = 极端价区训练样本少；中价区表现稳
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* K 近邻可解释性 */}
                <Card size="small" title={<span>🔍 K 近邻可解释性（spec §4.8 验收 ②：100% 新款带依据）</span>}>
                  <div style={{ fontSize: 11, color: '#595959', marginBottom: 12 }}>
                    候选 <code>{pls.knn_example.candidate_id}</code> · "{pls.knn_example.title}" 新款定价过程：
                  </div>
                  <Row gutter={12}>
                    <Col span={14}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                            <th style={{ padding: 6, textAlign: 'left' }}>相似款</th>
                            <th style={{ padding: 6 }}>相似度</th>
                            <th style={{ padding: 6 }}>纯金重</th>
                            <th style={{ padding: 6 }}>主石</th>
                            <th style={{ padding: 6 }}>副石</th>
                            <th style={{ padding: 6 }}>倍率</th>
                            <th style={{ padding: 6 }}>权重</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pls.knn_example.similar_items.map(s => (
                            <tr key={s.ref_id} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                              <td style={{ padding: 6, fontFamily: 'monospace' }}>{s.commodity_no}</td>
                              <td style={{ padding: 6, textAlign: 'center' }}>{(s.similarity * 100).toFixed(0)}%</td>
                              <td style={{ padding: 6, textAlign: 'center' }}>{s.pure_gold_weight_g}g</td>
                              <td style={{ padding: 6, textAlign: 'center' }}>${(s.main_stone_value_cents / 100).toFixed(0)}</td>
                              <td style={{ padding: 6, textAlign: 'center' }}>${(s.slave_stone_value_cents / 100).toFixed(0)}</td>
                              <td style={{ padding: 6, textAlign: 'center' }}>{s.multiplier}</td>
                              <td style={{ padding: 6, textAlign: 'center' }}>
                                <strong>{(s.weight_in_estimate * 100).toFixed(0)}%</strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Col>
                    <Col span={10}>
                      <Card type="inner" size="small" title="AI 预估值（加权合成）">
                        <div style={{ fontSize: 12, marginBottom: 6 }}>
                          纯金重: <strong>{pls.knn_example.estimated.pure_gold_weight_g}g</strong>
                          <small style={{ color: '#8c8c8c' }}> (借自相似款加权)</small>
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 6 }}>
                          主石值: <strong>${(pls.knn_example.estimated.main_stone_value_cents / 100).toFixed(2)}</strong>
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 8 }}>
                          副石值: <strong>${(pls.knn_example.estimated.slave_stone_value_cents / 100).toFixed(2)}</strong>
                        </div>
                        <Divider plain style={{ margin: '8px 0', fontSize: 10 }}>spec §4.8 物料合成</Divider>
                        <div style={{ fontSize: 10, color: '#595959', lineHeight: 1.6 }}>
                          syntheticCost = pureGoldWeight × goldPriceRealtime + handFee + mainStoneValue + Σ slaveStoneValue
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </>
            ),
          },

          // ============================================================
          // Tab 2：listing RAG 学习
          // ============================================================
          {
            key: 'listing',
            label: '② listing 学习',
            children: (
              <>
                <SpecCallout>
                  spec §4.7 缺反馈学习机制，2026-06-27 拍板用 <strong>RAG 范式</strong>：召回库存历史 listing → 新候选生成时按风格/品类/视觉特征召回 N=3 范本作 LLM few-shot 示例。<strong>不动 prompt 模板（spec §4.6 attention 上限原则），动召回库范本权重</strong>。反馈来源：① 运营 edit 后的版本 ② A/B winning variant ③ HIT 复盘
                </SpecCallout>
                <BizCallout>
                  这页解决"AI 写的 listing 怎么越来越像我们家品牌"。
                  上半部分召回库范本浏览看"AI 当前在跟哪些 listing 学"；
                  中间运营 edit pattern 揭示"AI 写得不够好的字段是哪些"；
                  底部 prompt 版本演进看每次 prompt 升级的实测收益。
                </BizCallout>

                {/* 召回库概况 */}
                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={4}><Card size="small"><Statistic title="召回库总量" value={lls.recall_pool_total} prefix={<FileTextOutlined />} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="高权重池" value={lls.high_weight_count} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="黑名单" value={lls.blacklist_count} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="平均相似度" value={(lls.avg_similarity * 100).toFixed(1)} suffix="%" /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="平均召回数" value={lls.avg_neighbor_count.toFixed(1)} suffix="近邻" /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="近 7d 召回次数" value={lls.high_weight_samples.reduce((s, x) => s + x.reach_count_7d, 0)} /></Card></Col>
                </Row>

                {/* 高权重池 */}
                <Card size="small" title={<span>🌟 高权重池范本（spec §4.6 范式：累积聚合给上游，不动 prompt）</span>} style={{ marginBottom: 12 }}>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="listing_id"
                    dataSource={lls.high_weight_samples}
                    columns={[
                      { title: '风格', dataIndex: 'style', render: s => <Tag color="blue">{s}</Tag> },
                      { title: '标题', dataIndex: 'title', render: t => <span style={{ fontSize: 11 }}>{t}</span> },
                      { title: '来源', dataIndex: 'source', render: (s: string) => {
                        const map: Record<string, { label: string; color: string }> = {
                          'HIT_RECAP': { label: '复盘 HIT', color: 'success' },
                          'AB_WINNER': { label: 'A/B winning', color: 'processing' },
                          'OPERATOR_EDIT_AFTER': { label: '运营 edit 后', color: 'cyan' },
                          'BASELINE': { label: '基线', color: 'default' },
                        };
                        const m = map[s] || { label: s, color: 'default' };
                        return <Tag color={m.color}>{m.label}</Tag>;
                      } },
                      { title: '权重', dataIndex: 'weight', render: w => <strong style={{ color: '#52c41a' }}>{w.toFixed(2)}</strong> },
                      { title: '7d 召回', dataIndex: 'reach_count_7d', render: n => `${n} 次` },
                      { title: '转化率', dataIndex: 'conversion_rate', render: r => `${(r * 100).toFixed(2)}%` },
                      { title: '范本片段', dataIndex: 'highlight_excerpt', render: t => <small style={{ color: '#595959' }}>{t}</small> },
                    ]}
                  />
                </Card>

                {/* 黑名单 */}
                <Card size="small" title="⚫ 黑名单范本（低转化 / MISS 复盘 → RAG 召回时降权）" style={{ marginBottom: 12 }}>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="listing_id"
                    dataSource={lls.blacklist_samples}
                    columns={[
                      { title: '风格', dataIndex: 'style', render: s => <Tag>{s}</Tag> },
                      { title: '标题', dataIndex: 'title', render: t => <span style={{ fontSize: 11 }}>{t}</span> },
                      { title: '权重', dataIndex: 'weight', render: w => <small style={{ color: '#cf1322' }}>{w.toFixed(2)}</small> },
                      { title: '转化率', dataIndex: 'conversion_rate', render: r => `${(r * 100).toFixed(2)}%` },
                      { title: '问题', dataIndex: 'highlight_excerpt', render: t => <small style={{ color: '#cf1322' }}>{t}</small> },
                    ]}
                  />
                </Card>

                {/* 运营 edit pattern */}
                <Card size="small" title="✏️ 运营 edit_listing 修改 pattern（AI 写不够好的字段）" style={{ marginBottom: 12 }}>
                  {lls.edit_patterns.map(e => (
                    <div key={e.field} style={{ marginBottom: 12, padding: 8, background: '#fafafa', borderRadius: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span><strong>{e.field}</strong> <Tag color={e.edit_rate > 0.3 ? 'red' : e.edit_rate > 0.15 ? 'orange' : 'green'}>{(e.edit_rate * 100).toFixed(0)}% 改</Tag></span>
                        <span style={{ fontSize: 11, color: '#8c8c8c' }}>{e.edit_count_30d} 次/30d</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#595959', marginTop: 4 }}><strong>共性:</strong> {e.common_pattern}</div>
                      <div style={{ fontSize: 11, marginTop: 6, display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1, padding: 6, background: '#fff5f5', borderLeft: '2px solid #cf1322', borderRadius: 2 }}>
                          <small style={{ color: '#cf1322' }}>before:</small> {e.example_diff.before}
                        </div>
                        <div style={{ flex: 1, padding: 6, background: '#f6ffed', borderLeft: '2px solid #52c41a', borderRadius: 2 }}>
                          <small style={{ color: '#52c41a' }}>after:</small> {e.example_diff.after}
                        </div>
                      </div>
                    </div>
                  ))}
                </Card>

                {/* prompt 版本演进 */}
                <Card size="small" title="📈 Prompt 版本演进 & 召回库累积">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="version"
                    dataSource={lls.prompt_version_history}
                    columns={[
                      { title: '版本', dataIndex: 'version', render: v => <Tag color="purple">{v}</Tag> },
                      { title: '上线日期', dataIndex: 'rolled_out_at' },
                      {
                        title: '首轮通过率', dataIndex: 'accept_rate_first_round',
                        render: r => (
                          <span>
                            <Progress percent={r * 100} size="small" style={{ width: 100, marginRight: 8 }} />
                            <strong>{(r * 100).toFixed(0)}%</strong>
                          </span>
                        ),
                      },
                      { title: 'A/B winning 累计', dataIndex: 'ab_winning_count' },
                      { title: '高权重池新增', dataIndex: 'high_weight_added', render: n => <span style={{ color: '#52c41a' }}>+{n}</span> },
                    ]}
                  />
                  <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                    💡 prompt 版本升级提升 = 首轮通过率上升 + 召回库高权重池累积 → AI 越上线越像品牌
                  </div>
                </Card>
              </>
            ),
          },

          // ============================================================
          // Tab 3：图片学习（戒指单品类）
          // ============================================================
          {
            key: 'image',
            label: '③ 图片学习（戒指）',
            children: (
              <>
                <SpecCallout>
                  spec §4.6 核心原则：<strong>抽卡范式下"打分模型反馈 prompt"边际收益趋零，prompt 已在 attention 上限</strong>。3 层质量回路（砍 1 留 2）：① CV 粗筛降级（让运营少看 70% 废图）② 拒图原因结构化→反馈给 prompt 模板（戒指品类）+ 选款降权 ③ 上架后效果回流→反馈给选款/风格/reference 图。柴进 2026-06-27 简化：单品类戒指
                </SpecCallout>
                <BizCallout>
                  这页解决"AI 出图怎么越来越准"。
                  拒图原因聚合看戒指品类<strong>哪类问题最多</strong>，反馈给 prompt 模板调整；
                  抽卡阈值动态调整看<strong>每款抽几张比较合适</strong>（戒指 spec 起始 3 张，根据实际拒率上下调）；
                  选款降权清单标"AI 生图难"的子风格，反馈给上游<strong>降权选款</strong>不要再选。
                </BizCallout>

                {/* CV 粗筛 + 抽卡阈值 */}
                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={8}>
                    <Card size="small" title="CV 粗筛准确率（spec §4.6 验收 ①）">
                      <Row gutter={8}>
                        <Col span={12}>
                          <Statistic
                            title="准确率"
                            value={(ils.cv_filter.accuracy * 100).toFixed(0)}
                            suffix="%"
                            valueStyle={{ color: ils.cv_filter.accuracy >= 0.9 ? '#52c41a' : '#fa8c16' }}
                          />
                          <div style={{ fontSize: 10, color: '#8c8c8c' }}>目标 ≥90%</div>
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="误剔好图率"
                            value={(ils.cv_filter.false_kill_rate * 100).toFixed(1)}
                            suffix="%"
                            valueStyle={{ color: ils.cv_filter.false_kill_rate < 0.05 ? '#52c41a' : '#cf1322' }}
                          />
                        </Col>
                      </Row>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 8 }}>
                        30d 样本: <strong>{ils.cv_filter.samples_30d.toLocaleString()}</strong> 张
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" title={<span>🎰 抽卡阈值（{ils.draw_threshold.category}）</span>}>
                      <Row gutter={4}>
                        <Col span={8}>
                          <Statistic title="spec 基线" value={ils.draw_threshold.spec_baseline} suffix="张" />
                        </Col>
                        <Col span={8}>
                          <Statistic title="当前激活" value={ils.draw_threshold.current_active} suffix="张" valueStyle={{ color: '#1890ff' }} />
                        </Col>
                        <Col span={8}>
                          <Statistic title="建议下次" value={ils.draw_threshold.suggested_next} suffix="张" valueStyle={{ color: '#52c41a' }} />
                        </Col>
                      </Row>
                      <div style={{ fontSize: 10, color: '#595959', marginTop: 8 }}>
                        💡 {ils.draw_threshold.reason}
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" title="spec §4.6 验收 4 项">
                      <ul style={{ fontSize: 11, paddingLeft: 16, color: '#595959', lineHeight: 1.8 }}>
                        <li>CV 粗筛准确率 ≥90% <Tag color="success">{(ils.cv_filter.accuracy * 100).toFixed(0)}%</Tag></li>
                        <li>每款平均抽卡 ≤5 张 <Tag color="success">{ils.draw_threshold.current_active}</Tag></li>
                        <li>运营每款审核 ≤2 分钟 <Tag color="warning">待埋点</Tag></li>
                        <li>拒图 reject_tag 填充率 ≥95% <Tag color="success">98%</Tag></li>
                      </ul>
                    </Card>
                  </Col>
                </Row>

                {/* 拒图原因聚合 */}
                <Card size="small" title="🏷️ 拒图原因聚合 30d（戒指品类）— 反馈分流路由" style={{ marginBottom: 12 }}>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="reason_code"
                    dataSource={ils.reject_aggregation.sort((a, b) => b.count_30d - a.count_30d)}
                    columns={[
                      { title: '原因码', dataIndex: 'reason_code', render: (c: RejectReasonCode) => <Tag>{c}</Tag> },
                      { title: '语义', render: (_, r: any) => REJECT_REASON_LABELS[r.reason_code as RejectReasonCode] },
                      {
                        title: '30d 计数', dataIndex: 'count_30d',
                        render: (n: number) => (
                          <span>
                            <Progress percent={(n / 200) * 100} size="small" style={{ width: 80, marginRight: 6 }} showInfo={false} />
                            <strong>{n}</strong>
                          </span>
                        ),
                      },
                      { title: '占比', dataIndex: 'rate', render: r => `${(r * 100).toFixed(1)}%` },
                      {
                        title: '趋势', dataIndex: 'trend',
                        render: t => {
                          if (t === 'up') return <Tag color="red" icon={<ArrowUpOutlined />}>上升</Tag>;
                          if (t === 'down') return <Tag color="green" icon={<ArrowDownOutlined />}>下降</Tag>;
                          return <Tag icon={<MinusOutlined />}>持平</Tag>;
                        },
                      },
                      {
                        title: '反馈给上游', dataIndex: 'feedback_to',
                        render: (f: string) => {
                          const labels: Record<string, { label: string; color: string }> = {
                            'prompt_template': { label: '戒指 prompt 模板', color: 'purple' },
                            'selection': { label: '选款降权', color: 'orange' },
                            'both': { label: '两个上游', color: 'magenta' },
                          };
                          const m = labels[f];
                          return <Tag color={m.color}>{m.label}</Tag>;
                        },
                      },
                    ]}
                  />
                  <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                    💡 spec §4.6 ② 路由规则：① BLURRY / CHAOTIC_RING / STONE_OFF 等画质问题 → 调 prompt 模板 ② STYLE_MISMATCH → 反馈选款（这类款 AI 抓不准风格） ③ OTHER → 同时给两个上游
                  </div>
                </Card>

                {/* 选款降权候选 */}
                <Card size="small" title={<span>🚫 选款降权清单（spec §4.6 ② 选款环节反馈）</span>}>
                  <div style={{ fontSize: 11, color: '#595959', marginBottom: 8 }}>
                    抽满 15 张 0 通过 → 标"AI 生图不可行"，反馈选款环节降权。这些子风格今后选款时<strong>不再优先抽取</strong>。
                  </div>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="style_subtag"
                    dataSource={ils.selection_downweight}
                    columns={[
                      { title: '子风格', dataIndex: 'style_subtag' },
                      { title: '累计尝试', dataIndex: 'total_attempts', render: n => `${n} 张` },
                      { title: '通过', dataIndex: 'pass_count', render: n => <strong>{n}</strong> },
                      { title: '通过率', dataIndex: 'pass_rate', render: r => <Tag color="red">{(r * 100).toFixed(1)}%</Tag> },
                      { title: '标记时间', dataIndex: 'flagged_at', render: (t: string) => new Date(t).toLocaleDateString('zh-CN') },
                    ]}
                  />
                </Card>
              </>
            ),
          },

          // ============================================================
          // Tab 4：效果回流（→ 选款）
          // ============================================================
          {
            key: 'effect',
            label: '④ 效果回流（→ 选款）',
            children: (
              <>
                <SpecCallout>
                  spec §4.6 ③ 上架后效果回流：跟"图好不好看"无关，反馈给<strong>选款 / 风格 / reference 图</strong>。流程：MEASURING（观察 7-30 天）→ LEARNED（HIT/MISS/PARTIAL 复盘）→ 反馈选款 source_type 权重 + 风格权重 + AI 模型偏好 + listing 召回库
                </SpecCallout>
                <BizCallout>
                  这页解决"AI 选什么款比较准"。
                  反馈链路图看<strong>每条 LEARNED 信号反馈给上游的哪些位置</strong>；
                  风格 / AI 模型 / source_type 的聚合数据告诉<strong>哪类款最容易跑出来</strong>；
                  source_type 权重调整建议是<strong>下个选款周期的具体动作</strong>。
                </BizCallout>

                {/* 反馈链路图 */}
                <Card size="small" title="🔄 反馈链路：MEASURING → LEARNED → 选款 4 上游" style={{ marginBottom: 12 }}>
                  <Timeline mode="left">
                    {efs.feedback_loop_flow.map((f, i) => (
                      <Timeline.Item
                        key={i}
                        color={f.from.includes('HIT') ? 'green' : f.from.includes('MISS') ? 'red' : f.from.includes('PARTIAL') ? 'orange' : 'blue'}
                        label={<span style={{ fontSize: 11 }}>{f.weight} 个候选</span>}
                      >
                        <div>
                          <code style={{ fontSize: 11, color: f.from.includes('HIT') ? '#52c41a' : f.from.includes('MISS') ? '#cf1322' : '#1890ff' }}>{f.from}</code>
                          <span style={{ margin: '0 8px' }}>→</span>
                          <strong style={{ fontSize: 12 }}>{f.to}</strong>
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{f.semantic}</div>
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </Card>

                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={12}>
                    <Card size="small" title="按风格的 HIT 率">
                      {efs.outcome_by_style.sort((a, b) => b.hit_rate - a.hit_rate).map(s => (
                        <div key={s.style} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <span><Tag color="blue">{s.style}</Tag> n={s.total}</span>
                            <span>
                              <strong style={{ color: s.hit_rate > 0.45 ? '#52c41a' : s.hit_rate > 0.3 ? '#1890ff' : '#fa8c16' }}>
                                {(s.hit_rate * 100).toFixed(0)}%
                              </strong>
                              <small style={{ color: '#8c8c8c', marginLeft: 4 }}>HIT {s.hit} · MISS {s.miss} · 局部 {s.partial}</small>
                            </span>
                          </div>
                          <Progress percent={s.hit_rate * 100} size="small" showInfo={false}
                            strokeColor={s.hit_rate > 0.45 ? '#52c41a' : s.hit_rate > 0.3 ? '#1890ff' : '#fa8c16'} />
                        </div>
                      ))}
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                        🎯 极简 / 森系 / 哥特命中率高 → 下季度选款偏重这 3 个风格
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="按 AI 模型的 HIT 率 & 平均 CVR">
                      {efs.outcome_by_model.sort((a, b) => b.hit_rate - a.hit_rate).map(m => (
                        <div key={m.model} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <span><Tag color="purple">{m.model}</Tag> n={m.total}</span>
                            <span>
                              HIT率 <strong style={{ color: m.hit_rate > 0.5 ? '#52c41a' : '#fa8c16' }}>{(m.hit_rate * 100).toFixed(0)}%</strong>
                              <small style={{ marginLeft: 6, color: '#8c8c8c' }}>CVR {(m.avg_cvr * 100).toFixed(1)}%</small>
                            </span>
                          </div>
                          <Progress percent={m.hit_rate * 100} size="small" showInfo={false} strokeColor="#722ed1" />
                        </div>
                      ))}
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                        🎯 Flux 2 + jewelry LoRA / NB Pro 命中率最高 → 提高这两个模型在抽卡中的占比
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* source_type 权重调整建议 */}
                <Card size="small" title="🎚️ Source Type 权重调整建议（下个选款周期）">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="source_type"
                    dataSource={efs.source_type_weight_adjust}
                    columns={[
                      { title: '来源', dataIndex: 'source_type', render: (s: SourceType) => <Tag color="cyan">{s}</Tag> },
                      { title: '当前权重', dataIndex: 'current_weight', render: w => `${(w * 100).toFixed(0)}%` },
                      { title: '30d 命中率', dataIndex: 'hit_rate_30d', render: r => `${(r * 100).toFixed(0)}%` },
                      { title: '建议权重', dataIndex: 'suggested_weight', render: w => <strong>{(w * 100).toFixed(0)}%</strong> },
                      {
                        title: 'Δ', dataIndex: 'delta',
                        render: d => (
                          <Tag color={d > 0 ? 'success' : d < 0 ? 'error' : 'default'}>
                            {d > 0 ? '+' : ''}{(d * 100).toFixed(0)}%
                          </Tag>
                        ),
                      },
                      { title: '依据', dataIndex: 'reason', render: r => <small style={{ color: '#595959' }}>{r}</small> },
                    ]}
                  />
                </Card>
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default FeedbackLearning;
